import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../utils/axios";
import CompanyInfoModal from "../components/CompanyInfoModal";
import PaymentCompletionModal from "../components/PaymentCompletionModal";
import { ProcessingOverlay } from "../components/ui";
import LayoutSkeleton from "../components/LayoutSkeleton";
import {
  resendSignupOTP,
  resendDeviceOTP,
  resendPasswordOTP,
  getUsage,
  getPermissions,
} from "../api/auth";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [switchingCompany, setSwitchingCompany] = useState(false);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [rzpPaymentProcessing, setRzpPaymentProcessing] = useState(false);

  // Organization-level state
  const [organization, setOrganization] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [activeCompany, setActiveCompany] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const isPaymentSuccessPage = location.pathname.includes("/payment/success");
  const isDashboardPage = location.pathname.includes("/dashboard");
  const isAuthPages =
    location.pathname.includes("/login") ||
    location.pathname.includes("/signup") ||
    location.pathname.includes("/forgot-password") ||
    location.pathname.includes("/reset-password");

  useEffect(() => {
    window.scroll({ top: 0, left: 0, behavior: "smooth" });
  }, [location.pathname]);

  // Initialize: restore auth from localStorage
  useEffect(() => {
    const token = localStorage.getItem("signing_token");
    const userData = localStorage.getItem("signing_user");
    if (token && userData) {
      setLoading(true);
      if (isAuthPages) {
        navigate("/dashboard");
      }
      getProfile();
    } else {
      setUser(null);
      setOrganization(null);
      setCompanies([]);
      setActiveCompany(null);
      setLoading(false);
    }
  }, []);

  // Fetch the list of companies the user has access to
  const fetchUserCompanies = useCallback(async () => {
    try {
      const response = await api.get("/user/companies");
      const companyList = response.data?.data?.companies || [];
      setCompanies(companyList);

      // Set active company from localStorage cache, or default to the most recently created
      const cachedId = localStorage.getItem("heysign_active_company_id");
      const cachedCompany = cachedId
        ? companyList.find((c) => c._id === cachedId)
        : null;
      const defaultCompany = cachedCompany || companyList[0] || null;

      if (defaultCompany) {
        setActiveCompany(defaultCompany);
        localStorage.setItem("heysign_active_company_id", defaultCompany._id);

        // Set organization from the default company
        if (defaultCompany.organization) {
          // setOrganization(defaultCompany.organization);
        } else if (defaultCompany.isOwner && defaultCompany.organizationId) {
          // fetch org if not embedded
          try {
            const orgRes = await api.get(`/user/organization`);
            if (orgRes.data?.data) setOrganization(orgRes.data.data);
          } catch {}
        }
      }

      return companyList;
    } catch (error) {
      console.error("Error fetching user companies:", error);
      return [];
    }
  }, []);

  // Switch active company — shows full-screen loader then re-fetches everything in one shot
  const switchCompany = useCallback(
    async (companyId) => {
      const target = companies.find((c) => c._id === companyId);
      if (!target) {
        console.warn("Company not found in list:", companyId);
        return;
      }
      // Set the new active company in localStorage FIRST so all subsequent
      // API calls (including getProfile) send the correct X-Company-ID header.
      localStorage.setItem("heysign_active_company_id", companyId);
      setActiveCompany(target);
      if (target.organization) {
        setOrganization(target.organization);
      }
      // Show full-screen loader while re-fetching profile, org, permissions, usage.
      // getProfile's own finally block calls setLoading(false) when done.
      setSwitchingCompany(true);
      setLoading(true);
      try {
        await getProfile();
      } catch {
        setLoading(false);
      } finally {
        setSwitchingCompany(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [companies],
  );

  // Helper: org has a pending payment waiting to be completed (Stripe OR Razorpay)
  const orgHasPendingPayment = (org) =>
    !org?.isSubscribed &&
    (org?.pendingCheckoutUrl || org?.razorpaySubscriptionId);

  // Determine whether to show the CompanyInfoModal (subscription gating).
  // Suppress it if a pending payment modal will be shown instead.
  useEffect(() => {
    if (!user) return;

    const org = organization;

    if (orgHasPendingPayment(org)) {
      setShowCompanyModal(false);
      return;
    }

    if (!org || !org.isSubscribed) {
      setShowCompanyModal(true);
    } else {
      setShowCompanyModal(false);
    }
  }, [user, organization]);

  // Razorpay payment processing indicator
  useEffect(() => {
    if (organization?.razorpay_payment_processing) {
      setRzpPaymentProcessing(true);
    } else {
      setRzpPaymentProcessing(false);
    }
  }, [organization]);

  // Poll for profile when Razorpay payment is processing
  useEffect(() => {
    if (!rzpPaymentProcessing) return;

    const interval = setInterval(async () => {
      try {
        await getProfile();
      } catch (_) {}
    }, 5000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rzpPaymentProcessing]);

  // Pending checkout payment modal — show for both Stripe (pendingCheckoutUrl) and
  // Razorpay (razorpaySubscriptionId) when the org is not yet subscribed.
  useEffect(() => {
    const hasPending =
      !organization?.isSubscribed &&
      (organization?.pendingCheckoutUrl ||
        organization?.razorpaySubscriptionId);
    if (hasPending && !isPaymentSuccessPage) {
      setShowPaymentModal(true);
    } else {
      setShowPaymentModal(false);
    }
  }, [
    organization?.pendingCheckoutUrl,
    organization?.razorpaySubscriptionId,
    organization?.isSubscribed,
    isPaymentSuccessPage,
  ]);

  const fetchUsageData = async () => {
    try {
      const usageData = await getUsage();
      setUsage(usageData.data);
    } catch (error) {
      console.error("Error fetching usage data:", error);
    }
  };

  const fetchPermissionsData = async () => {
    try {
      const permissionsData = await getPermissions();
      setPermissions(permissionsData?.permissions);
    } catch (error) {
      console.error("Error fetching permissions data:", error);
    }
  };

  const login = async (email, password) => {
    const response = await api.post("/auth/login", { email, password });
    const { token, user: userData } = response.data;

    if (!response?.data?.requiresOTP) {
      localStorage.setItem("signing_token", token);
      localStorage.setItem("signing_user", JSON.stringify(userData));
      setUser(userData);
      await getProfile();
    }

    return response?.data || null;
  };

  const logout = () => {
    localStorage.removeItem("signing_token");
    localStorage.removeItem("signing_user");
    localStorage.removeItem("heysign_active_company_id");
    setUser(null);
    setOrganization(null);
    setCompanies([]);
    setActiveCompany(null);
    setSubscription(null);
    setLoading(false);
    navigate("/login");
  };

  const verifyDevice = async (email, otp) => {
    try {
      const response = await api.post("/auth/verify-device", { email, otp });
      const { token, user: userData } = response.data;
      localStorage.setItem("signing_token", token);
      localStorage.setItem("signing_user", JSON.stringify(userData));
      setUser(userData);
      await getProfile();
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const signup = async (name, email, password, otp) => {
    try {
      if (otp) {
        const response = await api.post("/auth/verify-email", {
          name,
          email,
          password,
          otp,
        });
        return response.data;
      } else {
        const response = await api.post("/auth/signup", {
          name,
          email,
          password,
        });
        return response.data;
      }
    } catch (error) {
      throw error?.response?.data?.message || error?.message;
    }
  };

  const forgotPassword = async (email) => {
    try {
      const response = await api.post("/auth/forgot-password", { email });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const resetPassword = async (email, otp, newPassword) => {
    try {
      const response = await api.post("/auth/reset-password", {
        email,
        otp,
        newPassword,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const resendSignupOTPHandler = async (email) => resendSignupOTP(email);
  const resendDeviceOTPHandler = async (email) => resendDeviceOTP(email);
  const resendPasswordOTPHandler = async (email) => resendPasswordOTP(email);

  const verifyEmail = async (email, otp) => {
    try {
      const response = await api.post("/auth/verify-email", { email, otp });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const getProfile = async () => {
    try {
      const response = await api.get("/auth/profile");
      const { data } = response.data;

      const updatedUser = { ...user, ...data };
      localStorage.setItem("signing_user", JSON.stringify(updatedUser));
      setUser(updatedUser);
      setOrganization(updatedUser.organization);
      // After profile load, fetch companies list
      await fetchUserCompanies();

      return response.data;
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await api.put("/auth/profile", profileData);
      await getProfile();
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Refresh usage data whenever organization or active company changes
  useEffect(() => {
    if (organization?.isSubscribed) {
      fetchUsageData();
    }
  }, [organization?.isSubscribed, activeCompany?._id, location.pathname]);

  useEffect(() => {
    if (user) {
      fetchPermissionsData();
    }
  }, [user, location.pathname]);

  const value = {
    user,
    subscription,
    usage,
    permissions,
    loading,
    login,
    logout,
    verifyDevice,
    signup,
    forgotPassword,
    resetPassword,
    verifyEmail,
    updateProfile,
    getProfile,
    setUser,
    resendSignupOTP: resendSignupOTPHandler,
    resendDeviceOTP: resendDeviceOTPHandler,
    resendPasswordOTP: resendPasswordOTPHandler,
    // Organization & company context
    organization,
    setOrganization,
    companies,
    setCompanies,
    activeCompany,
    setActiveCompany,
    switchCompany,
    fetchUserCompanies,
  };

  const shouldShowSkeleton = loading;

  return (
    <AuthContext.Provider value={value}>
      {shouldShowSkeleton ? (
        <LayoutSkeleton
          message={
            switchingCompany
              ? "Switching company, please wait…"
              : "Please wait while we load your account..."
          }
        />
      ) : (
        <>
          {children}
          {showCompanyModal && !isPaymentSuccessPage && isDashboardPage && (
            <CompanyInfoModal
              isOpen={showCompanyModal}
              onClose={() => setShowCompanyModal(false)}
              planId="default_plan_id"
              companyInfo={activeCompany}
            />
          )}
          {showPaymentModal && (
            <PaymentCompletionModal
              isOpen={showPaymentModal}
              checkoutUrl={organization?.pendingCheckoutUrl}
              organization={organization}
              onLogout={logout}
              user={user}
            />
          )}
          <ProcessingOverlay
            isOpen={rzpPaymentProcessing}
            message="Your payment is being processed…"
            hint="Please wait while we confirm your payment."
          />
        </>
      )}
    </AuthContext.Provider>
  );
};
